import {
    Callout,
    CommandBarButton,
    IButtonStyles,
    IOverflowSetItemProps,
    IOverflowSetStyles,
    Icon,
    IconButton,
    OverflowSet,
    Stack,
    Text,
    keyframes,
    mergeStyles,
    useTheme
} from "@fluentui/react";
import { useId } from '@fluentui/react-hooks';
import { appStyles, noOp } from "global/Application";
import { CSSProperties, useRef, useState } from "react";
import { ECalloutTypes, ECardIcon, ICardProperties } from "./SimpleCard.types";

const onRenderItemStyles = {
    root: { padding: '1px' },
};

let fadeIn = keyframes({
    '50%': {
        opacity: 0.3,
    },

});




export default function SimpleCard(props: ICardProperties) {

    const theme = useTheme();
    const [isCalloutHidden, setIsCalloutHidden] = useState(true);

    const buttonId = useId('callout-button');

    const calloutTimeout = useRef<NodeJS.Timeout>();

    const colorMapping: Record<string, string> = {
        "SL": '#606060',
        "AR": '#606060',
        "FP": '#606060',
        "PL": '#606060',
        "Medical": theme.palette.magentaDark,
        "Pharmacy": theme.palette.blue,
        "Dental": theme.palette.blueMid,
        "Vision": theme.palette.yellowDark,
        "Telehealth": theme.palette.teal,
        "HRA": theme.palette.greenDark,
        "HSA": theme.palette.greenDark,
        "FSA": theme.palette.greenDark,
        "LSA": theme.palette.greenDark,
        "COBRA": theme.palette.redDark,
        "Ancillary": theme.palette.orange,
        "Primary Review": theme.palette.blueDark,
        "Secondary Review": theme.palette.yellowDark,
        "Marketing Review": theme.palette.magentaDark,
        "Approved": theme.palette.green,
        "Pending Issues": theme.palette.red,
    }

    //TODO: memoize this
    //TODO: check to see if this actually generates diff classname for each instance, can we share?
    const cardClassName = mergeStyles({
        height: "120px",
        width: "120px",
        cursor: "pointer",
        boxSizing: 'border-box',
        opacity: props.deactivated ? 0.3 : undefined,
        // backgroundColor: 'red',
    });

    const flasherClassName = mergeStyles({
        animation: `${fadeIn} 1.2s ease infinite`
    });

    const headerClassName = mergeStyles({
        position: 'absolute',
        backgroundColor: (props.header && colorMapping[props.header]) ?? '#606060',
        borderRadius: '2px',
        textAlign: 'center',
        color: 'white',
        paddingLeft: '5px',
        paddingRight: '5px',
        paddingTop: '2px',
        paddingBottom: '2px',
        marginTop: '8px',
        fontSize: '9px',
        left: '-10px',
        top: '-3px',
        textTransform: 'uppercase',
        minWidth: '3em',
    });


    const cardContentClassName = mergeStyles({
        cursor: "pointer",
        backgroundColor: theme.schemes?.soft?.semanticColors.menuBackground,
        borderTop: "4px solid gray",
        boxSizing: 'border-box',
        opacity: "1",
        width: "100%",
        height: "100%",
        textAlign: "center",
        transition: "0.2s",
        boxShadow: "0 2px 3px 0 rgba(0, 0, 0, 0.2)",
        borderRadius: "5px",
        '.card-hover-display': {
            width: "120px",
            display: 'none',
        },

        ':hover': {
            backgroundColor: theme.schemes?.soft?.semanticColors.menuItemBackgroundHovered,
            borderTop: "4px solid lightgray",
            color: theme.schemes?.soft?.semanticColors.menuItemText,
            '.card-hover-display': {
                display: 'inline',
            },
        },
    });




    const selectedStyle: CSSProperties = {}

    if (props.disabled) {
        selectedStyle.backgroundColor = 'gray';
        selectedStyle.borderTop = "4px solid darkgray";
        selectedStyle.color = "lightgray";
    }
    else if (props.selected === false) {
        // selectedStyle.backgroundColor = "darkgray";
        // selectedStyle.borderTop = "4px solid gray";

    } else if (props.selected === true) {
        selectedStyle.backgroundColor = theme?.palette.themePrimary;
        selectedStyle.borderTop = "4px solid lightblue";
        selectedStyle.borderTopColor = theme?.palette.themeSecondary;
        selectedStyle.color = theme.palette.white;
    }

    const overflowStyles: IOverflowSetStyles = {
        root: { display: 'flex', justifyContent: 'flex-end' },
        overflowButton: {}
    }

    const items: IOverflowSetItemProps[] = [];

    if (props.onCallout)
        items.push({
            key: 'info',
            name: '',
            icon: 'comment',
            onClick: noOp,
            onRender: () => {
                return (
                    <IconButton
                        id={buttonId}
                        iconProps={{ iconName: props.callout === ECalloutTypes.notification ? "Error" : "comment" }}
                        className={props.callout === ECalloutTypes.notification ? flasherClassName : undefined}
                        styles={{
                            root: { height: 'auto', width: 'auto', paddingTop: '3px' },
                            icon: {
                                fontSize: '10px',
                                color: props.callout === ECalloutTypes.notification ? theme.palette.white : theme.palette.black
                            }
                        }}
                        onMouseEnter={(e) => { setIsCalloutHidden(false); }}
                    >
                    </IconButton >
                );
            }
        });

    const onRenderFront = () => {
        if (props.onRenderFront)
            return props.onRenderFront();

        return (
            <Stack style={{ padding: theme.spacing.s1, marginTop: 'auto', marginBottom: 'auto', height: '100%' }} className="card-content-display">
                <Stack>

                    {/* {props.comparison && props.change &&
                        <Text variant="xSmall" styles={{ root: { color: "red" } }} >
                            {props.changeText !== "" ? props.changeText : "Has Changes"}
                        </Text>
                    } */}

                    {props.comparison && !props.change &&
                        <Text variant="xSmall" styles={{ root: { color: "green" } }}>
                            No Changes
                        </Text>
                    }

                    {props.labelText &&
                        <Text variant="xSmall">
                            {props.labelText}
                        </Text>
                    }

                </Stack>

                {props.title &&
                    <Text styles={{ root: { fontWeight: '700' } }}>
                        {props.title}
                    </Text>
                }

                {props.description &&
                    <Text styles={{ root: { overflowWrap: 'break-word', color: selectedStyle.color } }}>
                        {props.description}
                    </Text>
                }


                {props.subText &&
                    <Text variant="xSmall">
                        {props.subText}
                    </Text>
                }

            </Stack>
        );
    }

    const onRenderIcon = () => {
        switch (props.icons) {
            case ECardIcon.lock:
                return (
                    <div style={{ position: 'absolute', opacity: '0.1', marginLeft: '50px', marginTop: '50px', pointerEvents: 'none' }}>
                        <Icon iconName="LockSolid" style={{ fontSize: '60px' }} />
                    </div>
                );

            case ECardIcon.link:
                return (
                    <div style={{ position: 'absolute', opacity: '0.1', marginLeft: '50px', marginTop: '50px', pointerEvents: 'none' }}>
                        <Icon iconName="Link" style={{ fontSize: '60px' }} />
                    </div>
                );

            case ECardIcon.check:
                return (
                    <div style={{ position: 'absolute', opacity: '0.1', marginLeft: '50px', marginTop: '50px', pointerEvents: 'none' }}>
                        <Icon iconName="SkypeCheck" style={{ fontSize: '60px' }} />
                    </div>
                );

            case ECardIcon.copy:
                return (
                    <div style={{ position: 'absolute', opacity: '0.1', marginLeft: '50px', marginTop: '50px', pointerEvents: 'none' }}>
                        <Icon iconName="Copy" style={{ fontSize: '60px' }} />
                    </div>
                );

            case ECardIcon.add:
                return (
                    <div style={{ position: 'absolute', opacity: '0.1', marginLeft: '60px', marginTop: '60px', pointerEvents: 'none' }}>
                        <Icon iconName="Add" style={{ fontSize: '45px' }} />
                    </div>
                );

            default:
                return null;
        }
    }

    const onSelect = (event: any) => {
        if (props.disabled)
            return;

        props.onClick?.(event);
    }

    const onRenderHeaderOverflowSet = () => {
        return <OverflowSet
            role="menubar"
            styles={overflowStyles}
            onRenderOverflowButton={(props, defaultRender) => {
                const buttonStyles: Partial<IButtonStyles> = {
                    root: {
                        minWidth: 0,
                        padding: '0',
                        alignSelf: 'stretch',
                        height: 'auto',
                        width: 'auto',
                        backgroundColor: 'transparent'
                    },
                };

                return (
                    <CommandBarButton
                        ariaLabel="More items"
                        role="menuitem"
                        styles={buttonStyles}
                        menuIconProps={{ iconName: 'More' }}
                        menuProps={{ items: props! }} />
                );
            }}
            onRenderItem={item => {
                if (item.onRender) {
                    return item.onRender(item);
                }

                return (
                    <CommandBarButton
                        role="menuitem"
                        aria-label={item.name}
                        styles={onRenderItemStyles}
                        iconProps={{ iconName: item.icon }}
                        onClick={item.onClick} />
                );
            }}
            items={items}
            overflowItems={props.overflowItems}
        />;
    }

    const onRenderHeader = () => {
        if ((items?.length ?? 0) > 0 || (props.overflowItems?.length ?? 0) > 0 || props.header)
            return <div style={{ minHeight: '15px' }}>
                {props.header &&
                    <div style={{ position: 'relative', display: 'block' }}>
                        <div className={headerClassName}>{props.header}</div>
                    </div>
                }
                {onRenderHeaderOverflowSet()}
            </div>
    }

    return <div
        className={cardClassName}
        style={{ cursor: props.disabled ? 'default' : 'pointer' }}
    >
        <div style={{ position: 'relative' }}>
            {onRenderIcon()}
        </div>
        {props.onCallout && !isCalloutHidden &&
            <Callout
                className={appStyles.callout}
                gapSpace={0}
                target={`#${buttonId}`}
                onMouseEnter={() => {
                    if (calloutTimeout.current) {
                        clearTimeout(calloutTimeout.current);
                        calloutTimeout.current = undefined;
                    }

                }}
                onMouseLeave={() => { setIsCalloutHidden(true); }}
            >
                {onRenderHeaderOverflowSet()}
                {props.onCallout()}
            </Callout>
        }

        <div
            className={cardContentClassName}
            style={selectedStyle}
            tabIndex={props.disabled ? undefined : 0}
            onClick={onSelect}
            onKeyDown={(event) => {

                if (props.disabled)
                    return;

                // http://www.mit.edu/~rjc/aria/expandingSections.html#
                if (event.code === "Space" || event.code === "Enter") {
                    event.preventDefault();
                    onSelect(event);
                }
            }}
            onMouseLeave={(e) => {
                calloutTimeout.current = setTimeout(() => {
                    setIsCalloutHidden(true);
                }, 200);
            }}
            onMouseEnter={props.autoCallout
                ? (e) => setIsCalloutHidden(false)
                : (e) => e.altKey && setIsCalloutHidden(false)
            }
        >


            {onRenderHeader()}




            {/* {props.onHover &&
                <div style={{ position: 'absolute', maxWidth: '120px', width: '100%', height: '100%', backgroundColor: 'red', margin: '5px' }}>
                    <div className="card-hover-display">
                        {props.onHover()}
                    </div>
                </div>
            } */}

            {onRenderFront()}

            {props.onRenderFooter &&
                props.onRenderFooter()
            }
        </div>

    </div>;
}

